<script setup lang="ts">
    import { ref, provide, type Ref } from 'vue';
    // @ts-expect-error raison: lib externe mal typée
    import { defineProps, defineEmits, defineExpose, withDefaults } from 'vue';

    export interface CustomStepperStepEntry {
        uid: number;
        title: string;
    }

    export interface CustomStepperContext {
        steps: Ref<CustomStepperStepEntry[]>;
        activeIndex: Ref<number>;
        registerStep: (step: CustomStepperStepEntry) => void;
        unregisterStep: (uid: number) => void;
    }

    const props = withDefaults(defineProps<{ activeStep?: number }>(), { activeStep: 0 });
    const emit = defineEmits<{ (e: 'update:activeStep', value: number): void }>();

    const steps = ref<CustomStepperStepEntry[]>([]);
    const activeIndex = ref(props.activeStep);

    function registerStep(step: CustomStepperStepEntry) {
        steps.value.push(step);
    }

    function unregisterStep(uid: number) {
        steps.value = steps.value.filter((step) => step.uid !== uid);
    }

    function goTo(index: number) {
        if (index < 0 || index >= steps.value.length) return;
        activeIndex.value = index;
        emit('update:activeStep', index);
    }

    function next() {
        goTo(activeIndex.value + 1);
    }

    function previous() {
        goTo(activeIndex.value - 1);
    }

    provide<CustomStepperContext>('customStepper', { steps, activeIndex, registerStep, unregisterStep });

    defineExpose({ next, previous, goTo, activeIndex });
</script>
<template>
    <div class="stepper-navigation mb-6">
        <div class="stepper-steps">
            <template v-for="(step, index) in steps" :key="step.uid">
                <div
                    class="stepper-step"
                    :class="{ active: activeIndex === index, completed: activeIndex > index }"
                >
                    <div class="stepper-step-number">{{ index + 1 }}</div>
                    <div class="stepper-step-label">{{ step.title }}</div>
                </div>
                <div v-if="index < steps.length - 1" class="stepper-line" :class="{ completed: activeIndex > index }"></div>
            </template>
        </div>
    </div>
    <div class="stepper-content">
        <slot />
    </div>
</template>

<style scoped>
.stepper-navigation {
    width: 100%;
    margin-bottom: 2rem;
}

.stepper-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    max-width: 500px;
    margin: 0 auto;
}

.stepper-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    position: relative;
}

.stepper-step-number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1.1rem;
    background-color: rgba(100, 100, 100, 0.1);
    color: rgba(100, 100, 100, 0.6);
    border: 2px solid rgba(100, 100, 100, 0.2);
    transition: all 0.3s ease;
}

.stepper-step.active .stepper-step-number {
    background-color: rgb(var(--component-color-rgb), 1);
    color: var(--color-white);
    border-color: rgb(var(--component-color-rgb), 1);
    box-shadow: 0 0 0 0.4rem rgba(var(--component-color-rgb), 0.1);
}

.stepper-step.completed .stepper-step-number {
    background-color: rgb(var(--color-success-rgb), 1);
    color: var(--color-white);
    border-color: rgb(var(--color-success-rgb), 1);
}

.stepper-step-label {
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(100, 100, 100, 0.6);
    transition: all 0.3s ease;
}

.stepper-step.active .stepper-step-label {
    color: rgb(var(--component-color-rgb), 1);
    font-weight: 600;
}

.stepper-step.completed .stepper-step-label {
    color: rgb(var(--color-success-rgb), 1);
}

.stepper-line {
    flex: 1;
    height: 2px;
    background-color: rgba(100, 100, 100, 0.2);
    transition: all 0.3s ease;
}

.stepper-line.completed {
    background-color: rgb(var(--color-success-rgb), 1);
}

@media (max-width: 768px) {
    .stepper-steps {
        gap: 0.5rem;
    }

    .stepper-step-number {
        width: 35px;
        height: 35px;
        font-size: 1rem;
    }

    .stepper-step-label {
        font-size: 0.8rem;
    }
}
</style>
